import { Box, Checkbox, IconButton, Text, Tooltip, useClipboard, VStack } from '@chakra-ui/react';
import { t } from 'i18next';
import { DateTime } from 'luxon';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import {
  Column,
  Row,
  useFilters,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table';
import { CustomerSubscriptionsTableSubscriptionFragment } from '~components/organization/CustomerSubscriptionsTable/__generated__/CustomerSubscriptionsTable.graphql';
import { CopyIcon } from '~components/ui/icons';
import { defaultPageSizes as pageSizes } from '~components/ui/PageSizeSelector';
import { StopClickPropagation } from '~components/ui/StopClickPropagation';
import { generateTableCellComponent } from '~components/ui/TableCell';
import { WaveSubscriptionStatus } from '~graphql/__generated__/types';
import { Columns } from './types';

const TableCell = generateTableCellComponent<
  CustomerSubscriptionsTableSubscriptionFragment,
  ReactNode
>();

/** Cell component for the Valid Till column — shows date + months and days left */
const ValidTillCell = ({
  row: { original: subscription },
}: {
  row: Row<CustomerSubscriptionsTableSubscriptionFragment>;
}) => {
  const validDate = DateTime.fromISO(subscription.validUntil).toFormat('dd/MM/yy');
  const end = DateTime.fromISO(subscription.validUntil);
  const now = DateTime.now();
  const diff = end.diff(now, ['months', 'days']);
  const months = Math.floor(diff.months);
  const days = Math.floor(diff.days);

  const timeLeft =
    months > 0 ? `${months} Months left` : days > 0 ? `${days} Days left` : '0 Days left';

  return (
    <VStack alignItems="flex-start" spacing={0} overflow="hidden" width="100%">
      <Text fontSize="sm" whiteSpace="nowrap">
        {t('validTill', { defaultValue: 'Valid till' })}: {validDate}
      </Text>
      <Text fontSize="xs" color="gray.400" whiteSpace="nowrap">
        {timeLeft}
      </Text>
    </VStack>
  );
};

/** Cell component for the Subscription Name column — shows name + purchased on subtitle */
const SubscriptionNameCell = ({
  row: { original: subscription },
}: {
  row: Row<CustomerSubscriptionsTableSubscriptionFragment>;
}) => {
  const displayName = subscription.userGivenName ? subscription.userGivenName : subscription.name;

  const purchasedDate = DateTime.fromISO(subscription.createdAt).toFormat('dd/MM/yyyy');

  return (
    <VStack alignItems="flex-start" spacing={0} overflow="hidden" width="100%">
      <Text fontWeight="bold" fontSize="sm" noOfLines={1} width="100%">
        {displayName}
      </Text>
      <Text fontSize="xs" color="gray.400" whiteSpace="nowrap">
        {t('purchasedOnDate', {
          date: purchasedDate,
          defaultValue: `Purchased on ${purchasedDate}`,
        })}
      </Text>
    </VStack>
  );
};

/** Cell component for the Subscription ID with copy button */
const SubscriptionIdCell = ({
  row: { original: subscription },
}: {
  row: Row<CustomerSubscriptionsTableSubscriptionFragment>;
}) => {
  const { onCopy, hasCopied } = useClipboard(subscription.id);

  // HARDCODED: showing the first 10 characters of the ID, truncated
  const truncatedId =
    subscription.id.length > 10 ? `${subscription.id.slice(0, 10)}…` : subscription.id;

  return (
    <Box display="flex" alignItems="center" gap="1" whiteSpace="nowrap">
      <Tooltip label={subscription.id} placement="top">
        <Text fontSize="sm" color="gray.600" cursor="default" whiteSpace="nowrap">
          {truncatedId}
        </Text>
      </Tooltip>
      <Tooltip label={hasCopied ? 'Copied!' : 'Copy ID'} closeOnClick={false}>
        <IconButton
          aria-label="Copy subscription ID"
          icon={<CopyIcon />}
          size="xs"
          variant="ghost"
          onClick={onCopy}
        />
      </Tooltip>
    </Box>
  );
};

export function useCustomerSubscriptionsTable({
  data,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  displayCount,
  hideExpired = false,
  currentSubscriptionIds,
}: {
  data: CustomerSubscriptionsTableSubscriptionFragment[];
  displayCount: number;
  hideExpired: boolean;
  currentSubscriptionIds: string[];
}) {
  const [pendingRemovals, setPendingRemovals] = useState<Set<string>>(new Set());

  // The react-table docs specify that this *must* be memoized
  const columns = useMemo<Array<Column<CustomerSubscriptionsTableSubscriptionFragment>>>(
    () => [
      {
        id: Columns.Selection,
        width: '48px',
        Header: ({ getToggleAllPageRowsSelectedProps, page, toggleRowSelected, state }) => {
          const { role, title } = getToggleAllPageRowsSelectedProps();

          const selectableRows = page.filter(
            (row: Row<CustomerSubscriptionsTableSubscriptionFragment>) => {
              if (pendingRemovals.has(row.original.id)) return false;

              const isCurrentSubscription = currentSubscriptionIds.includes(row.original.id);
              return isCurrentSubscription || row.original.status === WaveSubscriptionStatus.Active;
            },
          );

          const selectedSelectableRowsCount = selectableRows.filter(
            (r: Row<CustomerSubscriptionsTableSubscriptionFragment>) => state.selectedRowIds[r.id],
          ).length;

          const isAllSelectableSelected =
            selectableRows.length > 0 && selectedSelectableRowsCount === selectableRows.length;
          const isSomeSelectableSelected =
            selectedSelectableRowsCount > 0 && !isAllSelectableSelected;

          const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const isChecked = e.target.checked;
            selectableRows.forEach((row: Row<CustomerSubscriptionsTableSubscriptionFragment>) => {
              toggleRowSelected(row.id, isChecked);
            });
          };

          return (
            <Checkbox
              isChecked={isAllSelectableSelected}
              isIndeterminate={isSomeSelectableSelected}
              onChange={handleChange}
              role={role}
              title={title}
            />
          );
        },
        Cell: ({ row }: { row: Row<CustomerSubscriptionsTableSubscriptionFragment> }) => {
          const { checked, indeterminate, onChange, role, title } = row.getToggleRowSelectedProps();
          const isCurrentSubscription = currentSubscriptionIds.includes(row.original.id);
          const isExpired = row.original.status !== WaveSubscriptionStatus.Active;
          const isPendingRemoval = pendingRemovals.has(row.original.id);

          const isDisabled = isPendingRemoval || (!isCurrentSubscription && isExpired);

          const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const isUnchecking = !e.target.checked;

            if (isUnchecking && isCurrentSubscription && isExpired) {
              setPendingRemovals((prev) => new Set([...prev, row.original.id]));
            }

            onChange?.(e);
          };

          return (
            <StopClickPropagation>
              <Checkbox
                isDisabled={isDisabled}
                isChecked={checked}
                isIndeterminate={indeterminate}
                onChange={handleChange}
                role={role}
                title={title}
              />
            </StopClickPropagation>
          );
        },
      },
      {
        id: Columns.Subscription,
        Header: t('subscriptionName'),
        accessor: 'name',
        Cell: SubscriptionNameCell,
        sortType: (rowA, rowB) => {
          const nameA = rowA.original.userGivenName ?? rowA.original.name;
          const nameB = rowB.original.userGivenName ?? rowB.original.name;
          return nameA.toLowerCase().localeCompare(nameB.toLowerCase());
        },
        width: '200px',
      },
      {
        id: Columns.Id,
        Header: t('subscriptionId'),
        accessor: 'id',
        Cell: SubscriptionIdCell,
        disableSortBy: true,
        width: '160px',
      },
      {
        id: Columns.Usage,
        Header: t('usage'),
        accessor: (subscription) =>
          `${subscription.usage.current}/${subscription.usage.max ?? '∞'}`,
        disableSortBy: true,
        Cell: TableCell,
        width: '150px',
      },
      {
        id: Columns.ValidUntil,
        Header: t('validTill', { defaultValue: 'Valid till' }),
        accessor: (subscription) =>
          DateTime.fromISO(subscription.validUntil).toFormat('dd/MM/yyyy'),
        Cell: ValidTillCell,
        sortType: (rowA, rowB) =>
          DateTime.fromISO(rowA.original.validUntil).toMillis() -
          DateTime.fromISO(rowB.original.validUntil).toMillis(),
        width: '140px',
      },
      {
        id: Columns.Customers,
        Header: t('customers'),
        accessor: (subscription) =>
          subscription.customers.length === 0 ? '-' : subscription.customers.length,
        Cell: TableCell,
        disableSortBy: true,
        width: '85px',
      },
    ],
    [currentSubscriptionIds, pendingRemovals],
  );

  // The react-table docs specify that this *must* be memoized
  // Because the `useSortBy` plugin for `react-table` is executed _before_ `useRowSelect`, we don't have access to `isSelected` when sorting is applied.
  // We do want to sort on selection state, however, so down below, where we memoize the data, we will be adding that property manually
  const dataMemoized = useMemo<CustomerSubscriptionsTableSubscriptionFragment[]>(() => {
    const filtered = hideExpired
      ? data.filter((subscription) => subscription.status === WaveSubscriptionStatus.Active)
      : data;

    return filtered;
  }, [data, hideExpired]);

  const selectedRowIds = useMemo(
    () =>
      currentSubscriptionIds.reduce<Record<string, boolean>>((map, current) => {
        map[current] = true;
        return map;
      }, {}),
    [currentSubscriptionIds],
  );

  // The react-table docs specify that this *must* be memoized
  const getRowId = useCallback((row: CustomerSubscriptionsTableSubscriptionFragment) => row.id, []);

  return useTable(
    {
      columns,
      data: dataMemoized,
      initialState: {
        pageSize: pageSizes[0],
        sortBy: [{ id: Columns.Subscription, desc: true }],
        selectedRowIds,
      },
      autoResetSortBy: false,
      autoResetFilters: false,
      autoResetSelectedRows: false,
      autoResetPage: false,
      autoResetGlobalFilter: false,
      getRowId,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
  );
}
